import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Parchment } from 'quill/core';
import IconButton from '../../components/IconButton';
import SVGIcon from '../../components/SVGIcon';
import { IS_EXTENSION } from '../../constants/platform';
import caretIcon from '../../icons/non-prefilled/16x16/caret.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import { useQuill } from '../QuillContext';
import deltaToText from '../utils/deltaToText';
import getHeaderCollapse from '../utils/getHeaderCollapse';
import useShowEditorToast from '../utils/useShowEditorToast';
import BasicHeader from './BasicHeader';
import styles from './Header.module.scss';
import HeaderId from './HeaderId';
import Scroll from './Scroll';
import { usePostLayoutSize } from '../../post/PostLayoutProvider';
import { isFeatureEnabled } from '../../utils/features';

interface HeaderButtonsProps {
  blot: Header;
  copyLinkDisabled: boolean;
}

const HeaderButtons = ({ blot, copyLinkDisabled }: HeaderButtonsProps) => {
  const { quill } = useQuill();
  const ref = useRef<HTMLDivElement | null>(null);
  const [lineHeight, setLineHeight] = useState<string | undefined>(undefined);
  const postLayoutSize = usePostLayoutSize();

  const shouldShowCopyLinkButton =
    !copyLinkDisabled &&
    (!isFeatureEnabled('new_post_layout') ||
      !['mobile', 'tablet'].includes(postLayoutSize));

  const shouldShowHeaderButtons =
    !isFeatureEnabled('new_post_layout') || postLayoutSize !== 'mobile';

  useEffect(() => {
    if (ref.current) {
      setLineHeight(getComputedStyle(ref.current).lineHeight);
    }
  }, []);

  useEffect(() => {
    if (!quill) return;
    getHeaderCollapse(quill)?.setIsCollapsible(shouldShowHeaderButtons);
  }, [quill, shouldShowHeaderButtons]);

  const showEditorToast = useShowEditorToast();

  if (!shouldShowHeaderButtons) return null;

  return (
    <div
      ref={ref}
      className={styles.buttonContainer}
      style={{ height: lineHeight }}
    >
      {shouldShowCopyLinkButton && (
        <IconButton
          className={classNames(styles.button, styles.linkButton)}
          onClick={async () => {
            const { location } = window;
            const headerAnchorLink = new URL(location.href);
            headerAnchorLink.hash = blot.getId();

            await navigator.clipboard.writeText(headerAnchorLink.toString());
            showEditorToast(
              { level: 'success', message: 'Link copied to clipboard' },
              2000,
            );
          }}
          testId="headerLinkButton"
          aria-label="Copy link to header"
        >
          {linkIcon}
        </IconButton>
      )}
      <IconButton
        className={classNames(styles.button, styles.collapseButton)}
        onClick={() => {
          if (quill) {
            getHeaderCollapse(quill)?.setHeaderCollapsed(blot);
          }
        }}
        testId="headerCollapseButton"
        aria-label="Toggle header collapse"
      >
        <SVGIcon className={styles.collapseButtonIcon}>{caretIcon}</SVGIcon>
      </IconButton>
    </div>
  );
};

const ID_LENGTH = 4;
const ID_PREFIX = 'h';
const ID_REGEX = /^h[0-9a-z]{4}-/;
export default class Header extends BasicHeader {
  static parseHeaderId(fragment: string) {
    if (fragment.match(ID_REGEX)) {
      const [id] = fragment.split('-', 2);
      return id;
    }
    return null;
  }

  static getSelectors(fragment: string) {
    const selectors = [`[id="${fragment}"]`, `[id$="-${fragment}"]`];
    const headerId = this.parseHeaderId(fragment);
    if (headerId) {
      selectors.push(`[id^="${headerId}-"]`);
    }
    return selectors;
  }

  static getUniqueId() {
    return `${ID_PREFIX}${Math.random()
      .toString(36)
      .substring(2, 2 + ID_LENGTH)}`;
  }

  id: string;
  // @ts-expect-error
  domNode: HTMLHeadingElement;
  protected copyLinkDisabled: boolean = false;

  constructor(scroll: Parchment.ScrollBlot, domNode: Node) {
    super(scroll, domNode);
    if (domNode instanceof HTMLElement) {
      domNode.classList.add(styles.header);
    }
    this.id = _.uniqueId();
    const uiNode = document.createElement('div');
    uiNode.dataset.noPrint = '';
    this.attachUI(uiNode);
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  renderPortal() {
    return (
      this.uiNode &&
      createPortal(
        <HeaderButtons
          blot={this}
          copyLinkDisabled={IS_EXTENSION || this.copyLinkDisabled}
        />,
        this.uiNode,
      )
    );
  }

  clone() {
    const clone = super.clone();
    if (clone.domNode instanceof HTMLElement) {
      clone.domNode.removeAttribute('id');
      if (HeaderId.value(clone.domNode)) {
        HeaderId.add(clone.domNode, Header.getUniqueId());
      }
    }
    return clone;
  }

  getId() {
    const text = _.kebabCase(this.getText());
    let slug = text
      .replace(/[.,/#!$%^&*;:{}=\-_`~() ]/g, '-') // replace punctuation https://stackoverflow.com/questions/4328500/how-can-i-strip-all-punctuation-from-a-string-in-javascript-using-regex
      .replace(/-{2,}/g, '-');
    const id = this.formats()[HeaderId.attrName];
    if (id) return `${id}-${slug}`;

    // Below is all for backwards compatibility prior to HeaderId support
    slug = _.trim(slug, '-') || 'h';
    if (/^\d/.test(slug)) {
      slug = `s-${slug}`; // selectors cannot start with number
    }
    const nodes = Array.from(
      document.querySelectorAll(`#${slug}, *[id^="${slug}-"]`),
    );
    const count = nodes.reduce((sum, node) => {
      if (
        // eslint-disable-next-line no-bitwise
        this.domNode.compareDocumentPosition(node) &
          Node.DOCUMENT_POSITION_PRECEDING ||
        !this.domNode.parentNode?.contains(node)
      ) {
        return sum + 1;
      }
      return sum;
    }, 0);
    return count === 0 ? slug : `${slug}-${count}`;
  }

  getText() {
    return deltaToText(this.delta()).trim();
  }

  optimize() {
    // @ts-expect-error
    super.optimize();
    const id = this.getId();
    if (this.domNode.getAttribute('id') !== id) {
      this.domNode.setAttribute('id', id);
    }
  }
}
